import type {RefArray, VariantRelation, Pointer} from '@wix/document-services-types'
import {REF_ARRAY_DATA_TYPE, RELATION_DATA_TYPES} from '../constants/constants'
import {addPrefix, removePrefix} from '@wix/document-manager-utils'
import _ from 'lodash'
import {isLocalVariant} from './variantsUtils'
import type {DalItem, DalValue} from '@wix/document-manager-core'

const REF_PREFIX = '#'
const stripHashIfExists = (query: string) => removePrefix(query, REF_PREFIX)
const addHash = (id?: string) => addPrefix(id, REF_PREFIX)
const {VARIANTS} = RELATION_DATA_TYPES

const refArray = {
    /**
     * Returns values property of provided object
     *
     * @param {Object} obj
     * @returns {Array}
     */
    extractValues: (obj: RefArray): string[] => _.get(obj, ['values'], []),
    /**
     * Returns values key of provided object stripping hashes(#)
     * @param {Object} obj
     * @returns {string[]}
     */
    extractValuesWithoutHash: (obj: RefArray): string[] => _.map(refArray.extractValues(obj), stripHashIfExists),
    /**
     * Returns reference array object
     * @param {Array} values
     * @returns {{values: Array, type: string}}
     */
    create: (values: any[] = []): Partial<RefArray> => ({
        type: REF_ARRAY_DATA_TYPE,
        values: _.map(values, v => (_.isString(v) ? `#${stripHashIfExists(v)}` : v))
    }),
    /**
     * returns new object, based on obj with provided values property
     *
     * @param {Object} obj
     * @param {Array} values
     * @returns {Object}
     */
    update: (obj: RefArray, values: any[]) => ({
        ...obj,
        values: _.map(values, v => (_.isString(v) ? `#${stripHashIfExists(v)}` : v))
    }),
    /**
     * Check if object has reference type
     *
     * @param {Object} obj
     * @returns {boolean}
     */
    isRefArray: (obj: RefArray): boolean => obj && obj.type === REF_ARRAY_DATA_TYPE
}

export interface VariantRelationPossiblySerialized extends Omit<VariantRelation, 'to' | 'remoteVariants' | 'id'> {
    to: DalValue
    remoteVariants?: string[]
}
const variantRelation = {
    create: (variants: string[], from: string, to: string | DalItem, separateRemoteVariants?: boolean) => {
        const result: VariantRelationPossiblySerialized = {
            type: VARIANTS,
            variants: _(variants)
                .filter((v: string | Pointer) => isLocalVariant(v, separateRemoteVariants))
                .map(v => (_.isString(v) ? `#${removePrefix(v, REF_PREFIX)}` : v))
                .value(),

            from: `#${from}`,
            to: _.isString(to) ? `#${to}` : to
        }
        if (!separateRemoteVariants) {
            return result
        }
        const remoteVariants = _(variants)
            .filter((v: string | Pointer) => !isLocalVariant(v, separateRemoteVariants))
            .map(v => (_.isString(v) ? `#${removePrefix(v, REF_PREFIX)}` : v))
            .value()
        if (remoteVariants.length > 0) {
            result.remoteVariants = remoteVariants
        }
        return result
    },
    extractVariants: (obj: VariantRelation): string[] | boolean =>
        obj?.variants ? _.map(obj.variants, stripHashIfExists) : false,

    extractRemoteVariants: (obj: VariantRelation): string[] | boolean =>
        obj?.remoteVariants ? _.map(obj.variants, stripHashIfExists) : false,

    extractAllVariants: (obj: VariantRelation): string[] | boolean => {
        const localVariants = obj?.variants ? _.map(obj.variants, stripHashIfExists) : false
        const remoteVariants = obj?.remoteVariants ? _.map(obj.remoteVariants, stripHashIfExists) : false
        if (localVariants && remoteVariants) {
            return [...remoteVariants, ...localVariants]
        }
        if (remoteVariants) {
            return remoteVariants
        }
        return localVariants
    },
    extractTo: (obj: VariantRelation): string => stripHashIfExists(obj?.to),
    extractFrom: (obj: VariantRelation): string => stripHashIfExists(obj?.from),
    isVariantRelation: (obj: Record<any, any> | undefined): obj is VariantRelation =>
        obj?.type ? obj.type === VARIANTS : false
}

export {refArray, variantRelation, stripHashIfExists, addHash}
